import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Pill, EmptyState, ReasonModal, ExportButton } from "./AdminUI";

const COLORS = ["#2E6B57", "#F59E0B", "#EF4444", "#94A3B8", "#3B82F6"];
const STATUS_LABEL = { verified: "Verified", in_progress: "In progress", action_required: "Action required — Pro Terms", lost: "Lost", suspended: "Suspended", overridden_inactive: "Overridden (inactive)" };
const FILTERS = ["all", "verified", "in_progress", "action_required", "lost", "suspended"];

export default function VerificationTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [notes, setNotes] = useState({});

  const load = () => api.get("/admin/verification", { params: filter !== "all" ? { status: filter } : {} }).then(r => setData(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const resend = async (uid) => {
    try { await api.post(`/admin/verification/${uid}/resend-email`); toast.success("Verification email resent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const recalc = async (uid) => {
    try { await api.post(`/admin/verification/${uid}/recalculate`); toast.success("Recalculated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const copyMissing = (row) => {
    navigator.clipboard?.writeText(row.missing_labels.join("\n"));
    toast.success("Missing requirements copied");
  };
  const addNote = async (uid) => {
    const text = notes[uid];
    if (!text?.trim()) return;
    try { await api.post(`/admin/verification/${uid}/note`, { text }); toast.success("Note added"); setNotes(n => ({ ...n, [uid]: "" })); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <p className="text-sm text-[#222]/45">Loading…</p>;

  return (
    <div data-testid="admin-verification-tab">
      <div className="flex justify-end mb-3"><ExportButton kind="professionals" label="Professionals" /></div>
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
          <h3 className="text-sm font-bold mb-3">Eligibility status</h3>
          {data.status_distribution.length === 0 ? <p className="text-sm text-[#222]/40 py-8 text-center">No Pro accounts yet</p> : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.status_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label>
                  {data.status_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
          <h3 className="text-sm font-bold mb-3">Most commonly missing requirements</h3>
          {data.top_missing_requirements.length === 0 ? <p className="text-sm text-[#222]/40 py-8 text-center">Nothing missing — great!</p> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.top_missing_requirements} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#C79B6B" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="flex gap-1 mb-4 flex-wrap">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === f ? "bg-[#222] text-white" : "bg-white border border-black/10"}`} data-testid={`admin-verification-filter-${f}`}>
            {f === "all" ? "All" : STATUS_LABEL[f]}
          </button>
        ))}
      </div>

      {data.profiles.length === 0 ? (
        <EmptyState title="No profiles" body="No pro profiles match this filter." testid="admin-verification-empty" />
      ) : (
        <div className="space-y-3">
          {data.profiles.map(row => (
            <div key={row.user_id} className="bg-white rounded-2xl p-4 shadow-sm border border-black/5" data-testid={`admin-verification-row-${row.user_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-sm flex items-center gap-2">
                    {row.business_name}
                    <Pill tone={row.status === "verified" ? "green" : row.status === "lost" ? "red" : (row.status === "suspended" || row.status === "action_required") ? "amber" : "neutral"}>{STATUS_LABEL[row.status] || row.status}</Pill>
                  </p>
                  <p className="text-xs text-[#222]/50">{row.email} · {row.completed}/{row.total} complete · {row.photo_count} portfolio photos</p>
                  {row.missing_labels.length > 0 && <p className="text-xs text-amber-700 mt-1">Missing: {row.missing_labels.join(", ")}</p>}
                  {row.lost_reason && <p className="text-xs text-red-600 mt-1">Lost reason: {row.lost_reason}</p>}
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {!row.email_verified && <button onClick={() => resend(row.user_id)} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold" data-testid={`admin-verification-resend-${row.user_id}`}>Resend email link</button>}
                  <button onClick={() => copyMissing(row)} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold">Copy missing</button>
                  <button onClick={() => recalc(row.user_id)} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold" data-testid={`admin-verification-recalc-${row.user_id}`}>Recalculate</button>
                  <button onClick={() => setModal(row)} className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 font-semibold" data-testid={`admin-verification-override-${row.user_id}`}>Override</button>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <input value={notes[row.user_id] || ""} onChange={e => setNotes(n => ({ ...n, [row.user_id]: e.target.value }))} placeholder="Internal support note…"
                  className="flex-1 text-xs px-3 py-1.5 border border-black/10 rounded-full outline-none" data-testid={`admin-verification-note-input-${row.user_id}`} />
                <button onClick={() => addNote(row.user_id)} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold">Add note</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ReasonModal title={`Override — ${modal.business_name}`} body="Manually activate or deactivate the Verified Profile badge. This is logged and should only be used when necessary."
          onConfirm={async (reason) => {
            try { await api.post(`/admin/verification/${modal.user_id}/override`, { action: modal.status === "verified" ? "deactivate" : "activate", reason }); toast.success("Override applied"); load(); }
            catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
          }}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
}
